import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

const AddEditReceiptConfig = ({
  open,
  onClose,
  onSave,
  receiptConfig,
  user,
  loading,
}) => {
  const [formData, setFormData] = useState({
    companyName: "",
    storeName: "",
    address: "",
    TIN: "",
    SN: "",
    MIN: "",
    storeNumber: "",
    storeEmail: "",
    birPermit: "",
    birPermitDate: "",
    birAccredNo: "",
    SN2: "",
    MIN2: "",
    birPermit2: "",
  });

  const [errors, setErrors] = useState({
    companyName: "",
    storeName: "",
    address: "",
    TIN: "",
    SN: "",
    MIN: "",
    storeNumber: "",
    storeEmail: "",
    SN2: "",
    MIN2: "",
  });

  const [originalData, setOriginalData] = useState(null);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);

  useEffect(() => {
    if (receiptConfig) {
      setFormData(receiptConfig);
      setOriginalData(receiptConfig);
    } else {
      const initialData = {
        companyName: user?.companyname || "",
        storeName: "",
        address: "",
        TIN: "",
        SN: "",
        MIN: "",
        storeNumber: "",
        storeEmail: "",
        birPermit: "",
        birPermitDate: "",
        birAccredNo: "",
        SN2: "",
        MIN2: "",
        birPermit2: "",
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [receiptConfig, user]);

  const validateField = (name, value) => {
    let error = "";

    if (value.trim() === "") {
      error = "This field is required.";
    } else {
      switch (name) {
        case "TIN":
          if (!/^\d{3}-\d{3}-\d{3}-\d{5}$/.test(value)) {
            error = "TIN must follow the format XXX-XXX-XXX-XXXXX.";
          }
          break;
        case "SN":
        case "MIN":
          if (!/^[A-Za-z0-9\-]+$/.test(value)) {
            error = `${name} must contain only alphanumeric characters or hyphens.`;
          }
          break;
        case "storeNumber":
          if (!/^[\+0-9]+$/.test(value)) {
            error = "Store number must contain only numbers and the + symbol.";
          }
          break;
        case "storeEmail":
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = "Email must be in a valid format.";
          }
          break;
        default:
          break;
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    validateField(name, value);
  };

  useEffect(() => {
    const isComplete =
      Object.values(formData).every((field) =>
        typeof field === "string"
          ? field.trim() !== ""
          : field !== null && field !== undefined
      ) && Object.values(errors).every((error) => error === "");

    const hasChanges =
      JSON.stringify(formData) !== JSON.stringify(originalData);

    setIsFormComplete(isComplete);
    setIsFormChanged(hasChanges);
  }, [formData, errors]);

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {receiptConfig
          ? "Edit Receipt Configuration"
          : "Add Receipt Configuration"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Company Name"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            fullWidth
            error={!!errors.companyName}
            helperText={errors.companyName}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Store Name"
            name="storeName"
            value={formData.storeName}
            onChange={handleChange}
            fullWidth
            error={!!errors.storeName}
            helperText={errors.storeName}
          />
          <TextField
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            fullWidth
            error={!!errors.address}
            helperText={errors.address}
          />
          <TextField
            label="TIN"
            name="TIN"
            value={formData.TIN}
            onChange={(e) => {
              const raw = e.target.value;
              const digitsOnly = raw.replace(/\D/g, "");

              let formatted = "";
              if (digitsOnly.length > 0) formatted += digitsOnly.slice(0, 3);
              if (digitsOnly.length >= 4)
                formatted += "-" + digitsOnly.slice(3, 6);
              if (digitsOnly.length >= 7)
                formatted += "-" + digitsOnly.slice(6, 9);
              if (digitsOnly.length >= 10)
                formatted += "-" + digitsOnly.slice(9, 14);

              if (formatted.length <= 17) {
                setFormData((prevData) => ({
                  ...prevData,
                  TIN: formatted,
                }));

                if (/^\d{3}-\d{3}-\d{3}-\d{5}$/.test(formatted)) {
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    TIN: "",
                  }));
                } else {
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    TIN: "TIN must follow the format XXX-XXX-XXX-XXXXX.",
                  }));
                }
              }
            }}
            fullWidth
            error={!!errors.TIN}
            helperText={errors.TIN || "Format: 123-456-789-00000"}
          />

          <TextField
            label="SN"
            name="SN"
            value={formData.SN}
            onChange={handleChange}
            fullWidth
            error={!!errors.SN}
            helperText={errors.SN}
          />
          <TextField
            label="SN2"
            name="SN2"
            value={formData.SN2}
            onChange={handleChange}
            fullWidth
            error={!!errors.SN2}
            helperText={errors.SN2}
          />
          <TextField
            label="MIN"
            name="MIN"
            value={formData.MIN}
            onChange={handleChange}
            fullWidth
            error={!!errors.MIN}
            helperText={errors.MIN}
          />
          <TextField
            label="MIN2"
            name="MIN2"
            value={formData.MIN2}
            onChange={handleChange}
            fullWidth
            error={!!errors.MIN2}
            helperText={errors.MIN2}
          />
          <TextField
            label="Store Contact Number"
            name="storeNumber"
            value={formData.storeNumber}
            onChange={handleChange}
            fullWidth
            error={!!errors.storeNumber}
            helperText={errors.storeNumber}
          />
          <TextField
            label="Store Email"
            name="storeEmail"
            value={formData.storeEmail}
            onChange={handleChange}
            fullWidth
            error={!!errors.storeEmail}
            helperText={errors.storeEmail}
          />
          <TextField
            label="BIR Permit"
            name="birPermit"
            value={formData.birPermit}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="BIR Permit 2"
            name="birPermit2"
            value={formData.birPermit2}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="BIR Permit Date"
            name="birPermitDate"
            type="date"
            value={formData.birPermitDate}
            onChange={handleChange}
            fullWidth
          />
          {/* <TextField
            label="BIR Accreditation No."
            name="birAccredNo"
            value={formData.birAccredNo}
            onChange={handleChange}
            fullWidth
          /> */}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={!isFormComplete || !isFormChanged || loading}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditReceiptConfig;
